// src/components/Contact.jsx
import React, { useState } from "react";
import styled from "styled-components";
import { Section as BaseSection } from "./sharedStyles";
import { addDoc, collection, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase";

/* Lucide Icons */
import { Mail, Phone, MapPin } from "lucide-react";

/* ---------- Section wrapper (dark) ---------- */
const Section = styled(BaseSection)`
  background: ${(p) =>
    p.theme.gradients?.bgSection ||
    `linear-gradient(180deg, ${p.theme.colors.bgDarker}, ${p.theme.colors.bgDark})`};
  color: ${(p) => p.theme.colors.text};
  padding: 48px 32px 16px;
`;

/* ---------- Grid: Equal columns ---------- */
const Grid = styled.div`
  margin-top: 22px;
  display: grid;
  grid-template-columns: 1fr 1fr; /* equal width */
  gap: 32px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const Form = styled.form`
  display: grid;
  gap: 14px;
  margin-top: 18px;
  grid-template-columns: repeat(2, minmax(0, 1fr));

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const Field = styled.label`
  display: grid;
  gap: 6px;
  color: ${(p) => p.theme.colors.text};
  font-size: 0.9rem;
  font-weight: 700;

  &:last-of-type {
    grid-column: 1 / -1;
  }

  input,
  textarea {
    width: 100%;
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 10px;
    padding: 12px 13px;
    background: rgba(255, 255, 255, 0.04);
    color: ${(p) => p.theme.colors.text};
    font: inherit;
    resize: vertical;
  }

  textarea { min-height: 118px; }
  input:focus, textarea:focus { border-color: ${(p) => p.theme.colors.primary}; outline: none; }
`;

const Submit = styled.button`
  grid-column: 1 / -1;
  justify-self: start;
  border: 0;
  border-radius: 10px;
  padding: 12px 18px;
  background: ${(p) => p.theme.gradients.cta};
  color: ${(p) => p.theme.colors.text};
  font: inherit;
  font-weight: 800;
  cursor: pointer;
  transition: transform 180ms ease, box-shadow 180ms ease;

  &:hover { transform: translateY(-3px); box-shadow: 0 14px 28px rgba(230, 57, 70, 0.22); }
  &:disabled { opacity: 0.65; cursor: wait; transform: none; }
`;

const FormStatus = styled.p`
  grid-column: 1 / -1;
  margin: 0;
  color: ${(p) => p.error ? p.theme.colors.secondary : p.theme.colors.primary};
`;

/* ---------- Left Card ---------- */
const InfoCard = styled.div`
  background: ${(p) =>
    p.theme.colors.card ||
    `linear-gradient(180deg, rgba(255,255,255,0.03), rgba(255,255,255,0.01))`};
  border-radius: 16px;
  padding: 22px;
  border: 1px solid rgba(255, 255, 255, 0.05);
  box-shadow: 0 8px 28px rgba(0, 0, 0, 0.45);
`;

const FormCard = styled(InfoCard)`
  margin-top: 32px;
`;

const Title = styled.h2`
  margin: 0 0 16px 0;
  font-size: 1.9rem;
  font-weight: 900;
  text-align: center;
  color: ${(p) => p.theme.colors.text};
`;

/* Label Row with Icon */
const Row = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-top: 22px;

  svg {
    width: 22px;
    height: 22px;
    color: ${(p) => p.theme.colors.white}; /* accent color */
    flex-shrink: 0;
  }
`;

const Label = styled.div`
  font-weight: 700;
  font-size: 1rem;
  color: ${(p) => p.theme.colors.text};
`;

const Value = styled.div`
  color: ${(p) => p.theme.colors.muted};
  font-size: 0.95rem;
  margin-top: 4px;

  a {
    color: ${(p) => p.theme.colors.muted};
    text-decoration: none;
  }
`;

/* ---------- Right: Map ---------- */
const MapWrapper = styled.div`
  width: 100%;
  height: 60vh;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 8px 28px rgba(0, 0, 0, 0.45);

  iframe {
    width: 100%;
    height: 100%;
    border: 0;
  }
`;

export default function Contact() {
  const [form, setForm] = useState({ name: "", phone: "", email: "", subject: "", message: "" });
  const [status, setStatus] = useState({ message: "", error: false, loading: false });

  const updateField = (event) => setForm((current) => ({ ...current, [event.target.name]: event.target.value }));

  const submitForm = async (event) => {
    event.preventDefault();
    setStatus({ message: "", error: false, loading: true });
    try {
      await addDoc(collection(db, "websiteQueries"), {
        ...form,
        status: "New",
        source: "Website Contact Form",
        receivedAt: serverTimestamp(),
      });
      setForm({ name: "", phone: "", email: "", subject: "", message: "" });
      setStatus({ message: "Thanks — your message has been sent. We’ll be in touch soon.", error: false, loading: false });
    } catch (error) {
      setStatus({ message: "We couldn’t send your message right now. Please call or email us directly.", error: true, loading: false });
    }
  };

  return (
    <Section id="contact">
      <Title>Contact Us</Title>

      <Grid>
        {/* LEFT — Contact Info */}
        <InfoCard>
          <p style={{ marginTop: 0, color: "var(--muted)" }}>
            We’re here to assist with audits, pilots, and collection operations.
          </p>

          {/* EMAIL */}
          <Row>
            <Mail />
            <div>
              <Label>Email</Label>
              <Value>
                <a href="mailto:Ksgm012020@gmail.com">Ksgm012020@gmail.com</a>
              </Value>
            </div>
          </Row>

          {/* PHONE */}
          <Row>
            <Phone />
            <div>
              <Label>Phone</Label>
              <Value>
                <a href="tel:+918510040069">+91 8510040060</a>
              </Value>
            </div>
          </Row>

          {/* ADDRESS */}
          <Row>
            <MapPin />
            <div>
              <Label>Registered Office Address</Label>
              <Value>
                1385 Tulsi Vihar, Dadri,
                <br />
                Gautambuddha Nagar,
                <br />
                Uttar Pradesh – 203207
              </Value>
            </div>
          </Row>
          <Row>
            <MapPin />
            <div>
              <Label>Operating Office Address</Label>
              <Value>
                1385 Tulsi Vihar, Dadri,
                <br />
                Gautambuddha Nagar,
                <br />
                Uttar Pradesh – 203207
              </Value>
            </div>
          </Row>
        </InfoCard>

        {/* RIGHT — Map */}
        <MapWrapper>
          <iframe
            title="K Shyam G Management registered office map"
            loading="lazy"
            allowFullScreen
            referrerPolicy="no-referrer-when-downgrade"
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3501.903284548555!2d77.6100000!3d28.6070000!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x390ceff0d8fa4dbf%3A0x0000000000000000!2s1385%20Tulsi%20Vihar%2C%20Dadri%2C%20UP!5e0!3m2!1sen!2sin!4v1700000000000"
          ></iframe>
        </MapWrapper>
      </Grid>

      <FormCard>
        <Title as="h2">Start a conversation</Title>
        <p style={{ margin: 0, color: "var(--muted)" }}>
          Tell us what you need help with and our team will respond shortly.
        </p>
        <Form onSubmit={submitForm}>
          <Field>Name<input name="name" value={form.name} onChange={updateField} required placeholder="Your name or company" /></Field>
          <Field>Phone<input name="phone" value={form.phone} onChange={updateField} required placeholder="+91 0000000000" /></Field>
          <Field>Email<input type="email" name="email" value={form.email} onChange={updateField} placeholder="you@example.com" /></Field>
          <Field>Subject<input name="subject" value={form.subject} onChange={updateField} placeholder="Partnership inquiry" /></Field>
          <Field>Message<textarea name="message" value={form.message} onChange={updateField} required placeholder="How can we help?" /></Field>
          {status.message && <FormStatus error={status.error}>{status.message}</FormStatus>}
          <Submit type="submit" disabled={status.loading}>{status.loading ? "Sending…" : "Send inquiry"}</Submit>
        </Form>
      </FormCard>
    </Section>
  );
}
